import windAvatar from '../Apps/assets/wind.jpg'
import sierraAvatar from '../Apps/assets/sierra.jpg'
import mediaAvatar from '../Apps/assets/media.jpg'
import chessAvatar from '../Apps/assets/chess.jpg'
import { WindIcon, SierraIcon, FilmIcon, ChessIcon } from '../Apps/icons'

export const MICROSITES = {
  wind: {
    id: 'wind',
    slug: 'wind',
    alternateSlugs: ['windsports', 'windsavant'],
    name: 'Windsports',
    tagline: 'Wind Intelligence for the Water',
    badge: 'Telegram Agent · Real-Time Spot & Wind Intelligence',
    themeColor: '#0284c7',
    themeGradient: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
    avatar: windAvatar,
    icon: WindIcon,
    handle: '@windsavant_bot',
    telegramUrl: 'https://t.me/windsavant_bot',
    leadParagraph:
      'Built specifically for wingfoilers, kiters, and windsurfers. Windsports puts real-time wind conditions, spot forecasts, gear recommendations, and daily morning briefings right at your fingertips.',
    overview:
      'Traditional weather apps are built for general city forecasts on land, not for what is happening out on the water. Windsports is built specifically for wingfoilers, kiters, and windsurfers, putting real-time wind conditions, spot forecasts, gear recommendations, and windsports-related information right at your fingertips. No complicated menus or technical jargon — just open Telegram to check if the wind is blowing at your favorite beach, see today\'s forecast, or find out what size wing, kite, or sail to rig for your weight.',
    previewQuotaNotice: 'Try 10 instant questions before approval — no sign-up or waitlist. Continued access after approval is completely free.',

    // Live Telegram chat preview demo
    demo: {
      userQuery: 'What is the wind forecast for Crissy Field this afternoon, and what wing should I rig at 82kg?',
      agentIntro: '💨 Crissy Field (SF Bay, CA) — Afternoon Wind Breakdown:',
      agentContent: [
        '• Current Actuals: 18 kts WNW, gusts to 23 kts (Presidio buoy @ 13:45). Flood tide slackening at 15:30.',
        '• Afternoon Peak: 22–27 kts from 16:00 to 18:30 as the marine layer pushes through the Golden Gate.',
        '• Rigging Recommendation (82kg Wing Foil): Rig a 4.0m wing with an 850cm² front foil. If the breeze surges past 26 kts under the bridge, depower or drop to a 3.5m.',
        '• Hazards: Strong ebb chop develops after 16:15; watch for container ship traffic in the center channel.'
      ],
      demoNote: 'Visual wind & gust chart delivered directly in chat'
    },

    pillars: [
      {
        title: 'Spot Forecasts for the Water',
        tagline: 'Where the wind actually blows — not inland averages',
        desc: 'Standard weather apps show general city weather. Windsports focuses directly on the water, pinpointing local beaches, coastal breezes, and thermal spot conditions.'
      },
      {
        title: 'Live Wind & Hourly Forecasts',
        tagline: 'Know what the wind is doing now and later today',
        desc: 'Check live wind speeds, gusts, and hourly forecasts before you head out, so you never miss a great session.'
      },
      {
        title: 'Gear & Quiver Advice',
        tagline: 'The right wing, kite, or sail size for your weight',
        desc: 'Tell Windsports your weight and riding style. It recommends the right gear size and setup for current conditions at any spot.'
      },
      {
        title: 'Daily Morning Briefings',
        tagline: 'Wake up to wind updates for your favorite spots',
        desc: 'Schedule automated daily reports in Telegram. Get a quick morning rundown of your favorite launches before you start your day.'
      },
      {
        title: 'Regional Wind Maps',
        tagline: 'Visual wind overviews of your area',
        desc: 'Ask for a wind map of your coast, bay, or lake. Windsports generates visual regional wind maps directly in chat.'
      },
      {
        title: 'Windsports & Spot Knowledge',
        tagline: 'Spots, rigging tips, and watersports lore',
        desc: 'Explore local spot guides, rigging tips, board setups, and knowledge from the global windsurfing, kiting, and wingfoiling community.'
      }
    ],

    samplePrompts: [
      'What is the wind forecast and gust spread for Crissy Field today?',
      'Show my favorite spots dashboard overview',
      'What size wing should an 80kg rider rig for 24-knot chop?',
      'Show regional wind map for Southern California',
      'Find wind spots near Hood River with afternoon thermal conditions',
      'Add Kanaha and Hatchery to my favorites dashboard',
      'What is the difference between high-aspect and mid-aspect hydrofoils in surf?',
      'Schedule my daily morning wind briefing for 07:00 PST'
    ],

    commands: [
      { cmd: '/forecast <spot>', desc: 'High-res spot wind forecast, live actuals & chart' },
      { cmd: '/dashboard', desc: 'Overview of all your saved favorite breaks' },
      { cmd: '/map <region>', desc: 'Generate high-detail regional wind & gust map' },
      { cmd: '/spots <region>', desc: 'Find active wind spots and current conditions' },
      { cmd: '/gear <knots>', desc: 'Sail, kite & wing sizing calibrated to your profile' },
      { cmd: '/alerts', desc: 'Set or manage daily morning wind briefings' },
      { cmd: '/memory', desc: 'Inspect your saved rider profile and quiver memory' },
      { cmd: '/trial', desc: 'Check your remaining try-out questions' },
      { cmd: '/request_access', desc: 'Request ongoing free access (1-tap approval)' }
    ]
  },

  sierra: {
    id: 'sierra',
    slug: 'sierra',
    alternateSlugs: ['sierraguide', 'sierra-weather'],
    name: 'Sierra Guide',
    tagline: 'Your Backcountry Authority for the High Country',
    badge: 'Telegram Agent · Mountain Weather & Backcountry Logistics',
    themeColor: '#059669',
    themeGradient: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
    avatar: sierraAvatar,
    icon: SierraIcon,
    handle: '@sierra_weather_bot',
    telegramUrl: 'https://t.me/sierra_weather_bot',
    leadParagraph:
      'An authoritative mountain weather forecaster, Doppler radar monitor, and trail logistics navigator for the High Sierra. Simply provide your location to get instant elevation-calibrated summit forecasts, live Doppler radar, and trail conditions in return. Off-grid? Contact us for WhatsApp-based access compatible with T-Mobile T-Satellite subscriptions.',
    overview:
      'Mountain weather in the Sierra Nevada can change in minutes, and low-altitude town forecasts fail to reflect what is happening thousands of feet above the valley floor. Sierra Guide lets you simply provide your location — whether a GPS coordinate, a trailhead pin, or a mountain pass — and get elevation-calibrated weather, live Doppler scans, and trail condition results in return. Heading deep into the backcountry beyond cellular range? Sierra Guide is also accessible via WhatsApp, making it fully compatible with T-Mobile T-Satellite subscriptions so you can stay informed off the grid. Contact us directly to activate WhatsApp-based access for your backcountry treks.',
    previewQuotaNotice: 'Try 10 instant questions before approval — no sign-up or waitlist. Continued access after approval is completely free.',
    satelliteNotice: 'Heading off the cellular grid? Contact us for WhatsApp-based access compatible with T-Mobile T-Satellite subscriptions to query Sierra Guide via satellite messaging deep in the backcountry.',

    demo: {
      userQuery: 'Planning Mount Whitney via the Mountaineers Route next weekend. What are the summit conditions and do I need a bear canister?',
      agentIntro: '🏔️ Mount Whitney (Summit Elev. 14,505 ft / Inyo National Forest):',
      agentContent: [
        '• Summit Weather: High 38°F, Low 21°F. Wind WNW 25–35 kts with gusts over 45 kts on the crest. Freezing level at 11,800 ft.',
        '• Radar & Storm Scan: Lightning CAPE index low (120 J/kg); afternoon convective thunderstorm risk is under 15%.',
        '• Route & Equipment: Snow and hard rime ice remain in the final 400 chute. Crampons and mountaineering axe mandatory.',
        '• Wilderness Regulations: Bear-resistant food canisters are legally MANDATORY year-round across the Whitney Zone. Counter-balancing food is strictly prohibited.',
        '• Permit Window: Inyo National Forest quota lottery is active. Overnight permits require Recreation.gov reservation.'
      ],
      demoNote: 'Topographic trail maps & Doppler scans generated on demand'
    },

    pillars: [
      {
        title: 'Location-Based Weather & Trails',
        tagline: 'Send your coordinates, pin, or trailhead for instant results',
        desc: 'Simply provide your current location, GPS coordinates, or trailhead pin. Sierra Guide immediately returns elevation-calibrated mountain forecasts, freezing levels, storm scans, and trail status tailored to your exact spot.'
      },
      {
        title: 'Off-Grid Satellite & WhatsApp Access',
        tagline: 'Use with T-Mobile T-Satellite when off the cellular grid',
        desc: 'Heading beyond cellular range? Contact us to activate WhatsApp access, allowing you to query Sierra Guide over satellite messaging via T-Mobile T-Satellite subscriptions deep in the wilderness.'
      },
      {
        title: 'Elevation-Calibrated Forecasts',
        tagline: 'Lapse-rate temperature & wind scaling at exact altitude',
        desc: 'Calculates weather directly for high alpine passes, summits, and cirques from 6,000 to 14,505 ft, avoiding deceptive valley floor assumptions.'
      },
      {
        title: 'Live Composite Doppler Radar',
        tagline: 'Convective thunderstorm & lightning risk scans',
        desc: 'Scans High Sierra radar mosaics to identify rapidly forming cumulonimbus cells, lightning hazards, and sudden summer afternoon monsoons.'
      },
      {
        title: 'Mountain Passes & Road Status',
        tagline: 'Live Caltrans & NPS highway chain control feeds',
        desc: 'Instant status for Tioga Pass (Hwy 120), Sonora Pass (Hwy 108), Monitor Pass (Hwy 89), Highway 180 to Cedar Grove, and Generals Highway.'
      },
      {
        title: 'Wilderness Permit Windows & Bear Rules',
        tagline: 'Rolling lotteries, quota dates & canister mandates',
        desc: 'Tracks permit openings for Mount Whitney, Half Dome, JMT, and Rae Lakes Loop, with verified food storage and bear canister regulations across wilderness boundaries.'
      }
    ],

    samplePrompts: [
      'Elevation-calibrated summit forecast for Mount Whitney at 14,505 ft',
      'Show composite Doppler radar scan for Yosemite Valley',
      'Is Tioga Pass open, and what are the current Caltrans chain controls?',
      'When does the 24-week rolling permit window open for Rae Lakes Loop?',
      'Show topographic trail map for Paradise Valley with canister rules',
      'What are the current USGS river flow rates for the Kings River at Cedar Grove?',
      'What is the fire restriction stage and camp stove policy in Inyo National Forest?',
      'Mileage, elevation gain, and route profile for Mount Langley via Cottonwood Lakes'
    ],

    commands: [
      { cmd: '/conditions <location>', desc: 'Road status, active fires, restrictions & river flows' },
      { cmd: '/weather <peak>', desc: 'Summit & high pass elevation-calibrated forecast' },
      { cmd: '/map <area>', desc: 'Topographic trail map with zoom control' },
      { cmd: '/radar', desc: 'Live composite Doppler radar & lightning thunderstorm scan' },
      { cmd: '/permits <park>', desc: 'Wilderness permit lottery windows and quota rules' },
      { cmd: '/roads', desc: 'Mountain pass status (Tioga, Sonora, Hwy 180)' },
      { cmd: '/trails', desc: 'Trail routes, elevation profiles & mandatory canister zones' },
      { cmd: '/memory', desc: 'View remembered trail preferences and home trailheads' },
      { cmd: '/trial', desc: 'Check your remaining try-out questions' },
      { cmd: '/request_access', desc: 'Request ongoing free access (1-tap approval)' }
    ]
  },

  media: {
    id: 'media',
    slug: 'media',
    alternateSlugs: ['movies', 'cinema'],
    name: 'Movies & Series',
    tagline: 'Your Cinema Concierge Across Streaming, Series & Cinema',
    badge: 'Telegram Agent · Entertainment Authority & Watchlist Engine',
    themeColor: '#dc2626',
    themeGradient: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
    avatar: mediaAvatar,
    icon: FilmIcon,
    handle: '@mediamoviesseries_bot',
    telegramUrl: 'https://t.me/mediamoviesseries_bot',
    leadParagraph:
      'A personal cinema concierge and streaming navigator. Movies & Series curates your watchlist, tracks new theater releases, uncovers hidden cinematic gems, and searches live streaming availability across Netflix, Amazon Prime, Max, Disney+, and Apple TV+.',
    overview:
      'Instead of endlessly scrolling through fragmented streaming apps or searching five different websites to see where a film is available, Movies & Series delivers instant answers right inside Telegram. Search any movie or television series for high-res posters, director filmographies, cast rosters, runtime, and verified streaming platforms. Manage your Watchlist and Favorites with single-tap interactive buttons.',
    previewQuotaNotice: 'Try 10 instant questions before approval — no sign-up or waitlist. Continued access after approval is completely free.',

    demo: {
      userQuery: 'Where can I stream Dune: Part Two right now, and recommend 3 cerebral sci-fi thrillers like Severance?',
      agentIntro: '🎬 Dune: Part Two (2024) [Movie] — ⭐ 8.5/10 (IMDb)',
      agentContent: [
        '• Director: Denis Villeneuve | Cast: Timothée Chalamet, Zendaya, Rebecca Ferguson',
        '• Streaming Availability: Streaming on Max (4K HDR, Dolby Atmos). Available to rent/buy on Apple TV & Amazon Prime Video.',
        '• Synopsis: Paul Atreides unites with Chani and the Fremen while seeking revenge against the conspirators who destroyed his family.',
        '',
        '✨ Recommendations based on Severance & cerebral tension:',
        '1. Dark (2017–2020) — Netflix · A time-loop mystery with impeccable structural plotting.',
        '2. Ex Machina (2014) — Max · Intimate psychological battle between creator, machine, and tester.',
        '3. Devs (2020) — Hulu · Quantum computing, determinism, and philosophical dread.'
      ],
      demoNote: 'Interactive buttons to add to Watchlist or mark Watched directly in Telegram'
    },

    pillars: [
      {
        title: 'Where-to-Watch Live Streaming Search',
        tagline: 'Never wonder which subscription has the movie',
        desc: 'Instant lookup of live availability across Netflix, Prime Video, Disney+, Max, Apple TV+, Paramount+, Peacock, and theatrical releases.'
      },
      {
        title: 'Interactive 1-Tap Watchlist & Favorites',
        tagline: 'Manage your queue with native Telegram inline actions',
        desc: 'Add to watchlist, mark as watched, favorite, or remove titles with a single tap. Complete list persistence across all your devices.'
      },
      {
        title: 'Deep Filmography & Franchise Lore',
        tagline: 'Directors, cinematographers, cast rosters, and trivia',
        desc: 'Powered by rich metadata covering release dates, production lore, Oscar histories, box office metrics, and interconnected cinematic universes.'
      },
      {
        title: 'Taste-Calibrated Personal Recommendations',
        tagline: 'Understands your favorite genres, tone, and directors',
        desc: 'Continuously learns your taste from your ratings, watched history, and chat feedback—suggesting hidden gems instead of generic algorithm filler.'
      },
      {
        title: 'Theatrical Box Office & New Premieres',
        tagline: 'What is hot in cinemas and streaming this week',
        desc: 'Weekly release schedules, festival winners, critical consensus, and trending titles updated in real-time.'
      },
      {
        title: 'Zero Algorithm Clutter',
        tagline: 'Clean, objective facts without sponsored recommendations',
        desc: 'No corporate promotion bias. Get honest synopses, real IMDb/TMDb user ratings, runtime, and curated alternatives.'
      }
    ],

    samplePrompts: [
      'Where can I stream Denis Villeneuve’s Dune: Part Two in 4K?',
      'Recommend 3 psychological sci-fi thrillers like Severance and Dark',
      'Show details, director, and full cast for Oppenheimer',
      'Add Shōgun to my watchlist and show what is currently in my queue',
      'What are the top-rated films released in 2024 with a rating above 8.0?',
      'Show my favorites list and mark Chernobyl as watched',
      'What movies are trending in theaters this weekend?',
      'Recommend an atmospheric neo-noir mystery from the 1990s'
    ],

    commands: [
      { cmd: '/recommendations', desc: 'Personalized movie & series recommendations' },
      { cmd: '/trending', desc: 'Explore hot new releases in theaters & streaming' },
      { cmd: '/watchlist', desc: 'View and manage your saved media watchlist' },
      { cmd: '/favorites', desc: 'View your favorite movie and television titles' },
      { cmd: '/watched', desc: 'Browse your watched film and TV history' },
      { cmd: '/search <title>', desc: 'Search title details, ratings, cast & streaming' },
      { cmd: '/memory', desc: 'Inspect your stored cinematic taste preferences' },
      { cmd: '/trial', desc: 'Check your remaining try-out questions' },
      { cmd: '/request_access', desc: 'Request ongoing free access (1-tap approval)' }
    ]
  },

  chess: {
    id: 'chess',
    slug: 'tichess',
    alternateSlugs: ['chess', 'tichess-bot'],
    name: 'TiChess',
    tagline: 'A Self-Learning Grandmaster Rival & Opening Theory Coach',
    badge: 'Telegram Agent · Adaptive Chess Engine & Repertoire',
    themeColor: '#6366f1',
    themeGradient: 'linear-gradient(135deg, #4f46e5 0%, #312e81 100%)',
    avatar: chessAvatar,
    icon: ChessIcon,
    handle: '@tichess_bot',
    telegramUrl: 'https://t.me/tichess_bot',
    leadParagraph:
      'A conversational Grandmaster sparring partner and opening coach who plays complete matches directly inside Telegram. Powered by an adaptive ELO-scaled chess engine with deep ECO theory, TiChess remembers your repertoire, analyzes pawn structures, renders visual boards on every turn, and coaches your tactical growth.',
    overview:
      'Most chess bots are silent calculation engines or basic web widgets. TiChess is designed to feel like sitting across the board from a knowledgeable Grandmaster friend in a club. It communicates conversationally, praises sharp tactical combinations, explains opening ideas (ECO theory), provides in-game tactical hints, and generates clean visual chessboard diagrams on every single move.',
    previewQuotaNotice: 'Try 10 instant questions before approval — no sign-up or waitlist. Continued access after approval is completely free.',

    demo: {
      userQuery: 'Let’s play from the Sicilian Defense: Najdorf Variation. I’ll play Black: 1. e4 c5 2. Nf3 d6 3. d4 cxd4 4. Nxd4 Nf6 5. Nc3 a6',
      agentIntro: '♟️ TiChess Grandmaster Coach — Sicilian Defense: Najdorf (B90)',
      agentContent: [
        'Solid choice! The Najdorf is one of the sharpest, most respected battlegrounds in chess history.',
        'By playing 5... a6, you prevent White’s pieces from landing on b5 and prepare queenside expansion with ...b5.',
        '',
        'My move: 6. Be3 (The English Attack).',
        'I am preparing f3, Qd2, and long castling (O-O-O) to launch an aggressive kingside pawn storm.',
        '',
        'Your turn! Popular master replies include 6... e5 (Classical Najdorf) or 6... e6 (Scheveningen structure). What is your move?'
      ],
      demoNote: 'Visual graphical chessboard diagram generated automatically on every turn'
    },

    pillars: [
      {
        title: 'Conversational Grandmaster Sparring',
        tagline: 'Plays full matches natively inside Telegram',
        desc: 'Send moves in standard algebraic notation (e4, Nf3, O-O) or descriptive language. TiChess calculates counter-moves and returns the game state instantly.'
      },
      {
        title: 'Dynamic ELO Engine Scaling',
        tagline: 'Spar as White or Black scaled directly to your skill level',
        desc: 'Self-calibrate your rating from beginner (800) to master (2400+). Engine depth and blunder likelihood scale dynamically to provide competitive sparring.'
      },
      {
        title: 'Live Graphical Chessboards',
        tagline: 'Renders board diagrams directly in chat on every turn',
        desc: 'Never play blind. TiChess generates visual board diagrams with piece coordinates, active turn indicators, and captured material counts on every single turn.'
      },
      {
        title: 'Deep ECO Opening Repertoire Prep',
        tagline: 'Comprehensive opening theory from Sicilian to King’s Indian',
        desc: 'Explore master lines, pawn structure strategies, tactical motifs, and common traps across hundreds of ECO opening classifications.'
      },
      {
        title: 'Real-Time Coaching & In-Game Hints',
        tagline: 'Ask for hints, evaluate blunders, and explore lines',
        desc: 'Stuck in a complex middle-game? Type "hint" for tactical guidance, undo a blunder with "undo", or ask TiChess to evaluate the position.'
      },
      {
        title: 'Personalized Repertoire Memory',
        tagline: 'Remembers your playstyle, blunders, and favorite lines',
        desc: 'TiChess tracks your win/loss record, opening preferences, and recurring tactical blind spots across sessions through persistent vector memory.'
      }
    ],

    samplePrompts: [
      'Start a new game with /newgame white matched to ELO 1400',
      'Let’s play the Sicilian Defense: Dragon Variation. I’ll play Black: 1. e4 c5',
      'I am in a tricky position on move 16, give me a tactical hint',
      'What is the main strategic idea behind the Queen’s Gambit Declined?',
      'Show my player profile, win/loss record, and current ELO rating',
      'Analyze the pawn structure in the French Defense: Winawer Variation',
      'Take back my last move and explain why it was a tactical blunder',
      'What are White’s best responses against the King’s Indian Defense?'
    ],

    commands: [
      { cmd: '/new white', desc: 'Start a new match playing as White' },
      { cmd: '/new black', desc: 'Start a new match playing as Black' },
      { cmd: 'move (e.g. e4, Nf3)', desc: 'Play your move in standard algebraic notation' },
      { cmd: 'hint', desc: 'Request tactical evaluation & coaching hint' },
      { cmd: 'undo', desc: 'Take back your last move and try an alternative line' },
      { cmd: 'show', desc: 'Re-render the active visual chessboard diagram' },
      { cmd: '/memory', desc: 'View your player profile, match record & ELO rating' },
      { cmd: '/trial', desc: 'Check your remaining try-out questions' },
      { cmd: '/request_access', desc: 'Request ongoing free access (1-tap approval)' }
    ]
  }
}
